const _ = require('lib/underscore')
const $ = require('lib/jquery')
const Marionette = require('lib/marionette')
const BaseModel = require('lib/base_model')
const Button = require('module/button/button')
const template = require('./location.html')
const styles = require('./location.less')
const edit_pane_styles = require('module/editpanel/edit_pane.less')
const triggerError = require('../trigger_error')
const DestroyOnEscape = require('behavior/destroy_on_escape')
const keycode = require('lib/keycode')
const config = require('lib/config')
const getCachedScript = require('lib/get_cached_script')
const radio = require('lib/radio')
const Raven = require('lib/raven')
const log = require('log').create('editpanel')

const default_error_string = 'Start typing your location and choose the closest match.'

const LocationErrorView = Marionette.View.extend({
    displayName: 'LocationErrorView',
    tagName: 'label',
    className: 'error',
    attributes: { for: 'user_location' },
    template: _.template(''),

    onRender() {
        if (this.options.text) {
            this.$el.text(this.options.text)
        }
    },
})

const LocationView = Marionette.View.extend({
    displayName: 'LocationView',

    template,

    styles: [edit_pane_styles, styles],

    behaviors: {
        DestroyOnEscape: DestroyOnEscape.config(),
    },

    initialize(options) {
        this.user = options.user

        _.bindAll(this, 'setUserLocation', 'getSelectedPlace', 'onSaveLocationError', 'triggerAutoComplete', 'destroy')

        this.triggerAutoComplete = _.debounce(this.triggerAutoComplete, 300)

        this.initializeModel()

        this.initializeListeners()
    },

    initializeModel() {
        // set up view_model
        let current_loc = this.user.get('locations').first()
        current_loc = current_loc ? current_loc.attributes : ''

        this.view_model = new BaseModel(
            {
                gmaps_loaded: '',
                new_location: current_loc,
                saved_location: current_loc,
            },
            {
                validators: {
                    new_location(value) {
                        if (this.get('gmaps_loaded')) {
                            // google places loaded; force user to select through google result

                            if (!value) return default_error_string
                            if (!value.location) return default_error_string
                            if (value.source !== 'google_string') return default_error_string
                        } else {
                            // accept freeform data for users in china or when library failed to load
                            if (!value) return 'Please enter your location.'
                        }
                    },
                },
            },
        )

        // set up button Model
        this.button = new Button.Model({
            text: 'Save',
            size: 'large',
            color: 'dark primary',
            className: 'fullwidth',
        })
    },

    initializeListeners() {
        this.listenTo(this.user.get('locations'), 'reset add remove', this.onChangeUserLocations)

        this.listenTo(this.view_model, 'change:new_location', (model, new_location) => {
            // display on profile when there is a location update
            this.user.get('locations').set(new_location)
        })

        // Commenting out the code below because the location autocomplete benefit isn't
        // currently worth the cost.
        /*
        // check to see google.maps API has been load
        const is_loaded = !! (typeof google !== 'undefined' && window.google.maps && window.google.maps.places)

        // only load google libraries when needed
        if (! is_loaded) {

            this.initializeCallbacks()
            this.loadGoogle()

        } else {
            this.view_model.set('gmaps_loaded', true)
        }
        */
    },

    loadGoogle() {
        // load google places api only when user is outside of China
        let script_url = '//maps.googleapis.com/maps/api/js?libraries=places&callback=__googlePlacesLoad'
        const key = config.get('pumpkinhead.googleplaces.key')
        script_url = `${script_url}&key=${key}`

        getCachedScript(script_url).fail(() => {
            this.view_model.set('gmaps_loaded', false)
        })
    },

    initializeCallbacks() {
        // create global callbacks for use by third-party scripts
        // using double-underscores to ensure name safety

        window.__googlePlacesLoad = () => {
            this.view_model.set('gmaps_loaded', true)
        }
    },

    ui: {
        input: 'input',
    },

    regions: {
        actions: '.actions',
        error: '.error_region',
    },

    events: {
        'click @ui.input': 'selectSearchText',
        'input @ui.input': 'onInput',
        'keypress @ui.input': 'onKeypress',
        'click .button': 'onClickButton',
        'click .back': 'destroy',
        'blur @ui.input': 'onBlur',
    },

    onRender() {
        this.prefillLocation()

        this.showChildView(
            'actions',
            new Button.View({
                model: this.button,
            }),
        )
    },

    prefillLocation() {
        const location = this.user.get('locations').first()

        if (location) {
            const value = location.get('location')
            this.ui.input.val(value)
        }
    },

    triggerAutoComplete() {
        log('triggerAutoComplete')
        if (this.isDestroyed()) return
        try {
            // passing an object as the last argument prevents `Uncaught
            // TypeError: Cannot set property 'handled' of undefined` from being
            // thrown
            window.google.maps.event.trigger(this.ui.input.get(0), 'focus', {})
        } catch (err) {
            Raven.captureException(err)
        }
    },

    onAttach() {
        this.ui.input.focus()
    },

    selectSearchText(e) {
        const input = this.ui.input.get(0)
        if (input && _.isDefined(input.selectionStart)) {
            input.selectionStart = 0
            input.selectionEnd = 999
        } else {
            this.ui.input.select()
        }
    },

    setUserLocation() {
        // if user location is not changed, skip save on server
        if (_.isEqual(this.view_model.get('saved_location'), this.user.get('locations').first().attributes)) {
            this.destroy()
            return
        }

        this.button.set({
            disabled: true,
            loading: true,
            text: 'Saving…',
        })

        this.user
            .get('locations')
            .save({ wait: true })
            .done(() => {
                // show confirmation message on button
                this.button.set({
                    loading: false,
                    text: 'Saved!',
                })
                // update view_model saved_location for later destroy check
                this.view_model.set('saved_location', this.user.get('locations').first().attributes)

                radio.metrics.trigger('location:saved')
                // close the screen after a short delay
                _.delay(this.destroy, 500)
            })
            .fail(this.onSaveLocationError)
    },

    onSaveLocationError(err) {
        this.button.set({
            disabled: false,
            loading: false,
            text: 'Save',
        })
        this.showChildView('error', new LocationErrorView({ text: err.statusText }))
        triggerError(err)
    },

    onInput() {
        // this "fixes" the input event being triggered without any actions
        // when placeholder changes - IE bug only
        if (!this.ui.input.is(':focus')) return

        this.getRegion('error').empty()
        this.view_model.unset('place_changed')

        const value = this.ui.input.val().trim() || ''
        const new_loc = {
            source: 'google',
            location: value,
        }

        if (this.view_model.get('gmaps_loaded')) {
            // initiate google autocomplete when user types to avoid dropdown on page load
            if (!this.service) this.setupGMapsAutocomplete()
            // reset user's locations when input changes and only saves when they select one
            // from the google result list
            this.view_model.set('new_location', '')
        } else {
            // when freeform, reset user obj with new input when user types;
            // but only saves on node when they are about to go to next screen;
            if (new_loc.source !== 'user') new_loc.source = 'user'
            this.view_model.set({
                new_location: new_loc,
                place_changed: true,
            })
        }
    },

    onBlur(e) {
        this.view_model.set('place_changed', true)
    },

    onKeypress(e) {
        if (e.which === keycode.ENTER) {
            this.save()
        }
    },

    onClickButton() {
        this.save()
    },

    setupGMapsAutocomplete() {
        const options = {
            types: ['(regions)'],
            fields: ['place_id', 'name', 'types'],
        }
        this.service = new window.google.maps.places.Autocomplete(this.ui.input.get(0), options)
        window.google.maps.event.addListener(this.service, 'place_changed', this.getSelectedPlace)
    },

    getSelectedPlace() {
        const place = this.service.getPlace()

        const location = {
            source: 'google_string',
            location: place.name,
        }

        this.view_model.set({
            new_location: location,
            place_changed: true,
        })
    },

    save() {
        // to differentiate enter key events from selected from dropdown and save button hit
        if (!this.view_model.get('place_changed')) return

        if (this.view_model.isValid()) {
            this.setUserLocation()
        } else {
            this.showChildView('error', new LocationErrorView({ text: this.view_model.validationError }))
        }
    },

    onBeforeDestroy() {
        const location = this.user.get('locations')
        // if change has not been updated on server, restore the original location
        if (!location.length || !_.isEqual(this.view_model.get('saved_location'), location.first().attributes)) {
            this.user.get('locations').reset(this.view_model.get('saved_location'))
        }
        // make sure autocomplete is turned off;
        if (this.service) {
            window.google.maps.event.trigger(this.ui.input.get(0), 'blur')
            window.google.maps.event.clearInstanceListeners(this.service)
            $('.pac-container').remove()
        }
        // reset service since we only initiate google service when service is not set
        delete this.service

        delete window.__googlePlacesLoad
    },
})

module.exports = LocationView
